// ============================================================
// PlaybackControls — with BPM, volume sliders, metronome
// ============================================================

import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import type { PlaybackCursor } from '../../core/playbackEngine';

export type SynthMode = 'guitar' | 'piano';

interface PlaybackControlsProps {
  playing:        boolean;
  isLoading:      boolean;
  cursor:         PlaybackCursor | null;
  tempo:          string | null;
  onToggle:       () => void;
  onStop:         () => void;
  showClick:      boolean;
  onToggleClick:  () => void;
  synthMode:      SynthMode;
  onSynthMode:    (mode: SynthMode) => void;
  onTempoChange:  (tempo: string) => void;
  chordVolume:    number;
  onChordVolume:  (v: number) => void;
  clickVolume:    number;
  onClickVolume:  (v: number) => void;
}

const SYNTH_OPTIONS: { value: SynthMode; label: string }[] = [
  { value: 'guitar', label: 'Guitar' },
  { value: 'piano',  label: 'Piano'  },
];

function parseBpmFromTempo(tempo: string | null): number {
  if (!tempo) return 120;
  const match = tempo.match(/(\d+)/);
  return match ? parseInt(match[1], 10) : 120;
}

// Small inline volume slider used for both chord and click controls.
function VolumeSlider({
  value,
  onChange,
  title,
  color = 'var(--color-accent)',
}: {
  value:    number;
  onChange: (v: number) => void;
  title:    string;
  color?:   string;
}) {
  return (
    <div title={title} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
      {/* Speaker icon */}
      <svg
        width="10" height="10" viewBox="0 0 12 12"
        fill="currentColor"
        style={{ color: 'var(--color-ink-muted)', flexShrink: 0 }}
      >
        <path d="M1 4h2l3-3v10L3 8H1V4z" />
        {value > 0.05 && <path d="M8 3.5a3.5 3.5 0 010 5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />}
      </svg>
      <input
        type="range"
        min={0}
        max={1}
        step={0.01}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        onKeyDown={(e) => e.stopPropagation()}
        style={{
          width:       44,
          height:      3,
          cursor:      'pointer',
          accentColor: color,
        }}
      />
    </div>
  );
}

export function PlaybackControls({
  playing,
  isLoading,
  onToggle,
  onStop,
  showClick,
  onToggleClick,
  synthMode,
  onSynthMode,
  tempo,
  onTempoChange,
  chordVolume,
  onChordVolume,
  clickVolume,
  onClickVolume,
}: PlaybackControlsProps) {
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);

  const [bpmInput, setBpmInput] = useState(() => String(parseBpmFromTempo(tempo)));

  useEffect(() => {
    setBpmInput(String(parseBpmFromTempo(tempo)));
  }, [tempo]);

  const commitBpm = useCallback(() => {
    const n = parseInt(bpmInput, 10);
    if (!isNaN(n) && n >= 20 && n <= 400) {
      onTempoChange(String(n));
    } else {
      setBpmInput(String(parseBpmFromTempo(tempo)));
    }
  }, [bpmInput, tempo, onTempoChange]);

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top:  rect.bottom + window.scrollY + 6,
        left: rect.left + window.scrollX,
      });
    }
    setOpen((v) => !v);
  };

  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      const target = e.target as Node;
      const portal = document.getElementById('instrument-dropdown-portal');
      if (buttonRef.current?.contains(target)) return;
      if (portal?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const dropdown = open ? ReactDOM.createPortal(
    <div
      id="instrument-dropdown-portal"
      style={{
        position:  'absolute',
        top:       dropdownPos.top,
        left:      dropdownPos.left,
        zIndex:    9999,
        background: 'var(--color-paper)',
        border:    '1px solid var(--color-rule)',
        borderRadius: 6,
        boxShadow: '0 4px 24px -4px rgba(0,0,0,0.18)',
        padding:   '6px 0',
        width:     160,
      }}
    >
      {/* Instrument options */}
      {SYNTH_OPTIONS.map((opt) => (
        <button
          key={opt.value}
          onClick={() => { onSynthMode(opt.value); setOpen(false); }}
          style={{
            width: '100%', textAlign: 'left',
            padding: '6px 12px',
            fontSize: 12, fontFamily: 'var(--font-mono)',
            background: synthMode === opt.value
              ? 'rgba(var(--color-accent-rgb), 0.1)'
              : 'transparent',
            color: synthMode === opt.value
              ? 'var(--color-accent)'
              : 'var(--color-ink-secondary)',
            border: 'none', cursor: 'pointer',
            transition: 'background 120ms',
          }}
          onMouseEnter={(e) => {
            if (synthMode !== opt.value)
              (e.currentTarget as HTMLElement).style.background = 'var(--color-hover)';
          }}
          onMouseLeave={(e) => {
            if (synthMode !== opt.value)
              (e.currentTarget as HTMLElement).style.background = 'transparent';
          }}
        >
          {opt.value === 'guitar' ? '🎸 ' : '🎹 '}{opt.label}
        </button>
      ))}

      {/* Divider */}
      <div style={{ borderTop: '1px solid var(--color-rule)', margin: '4px 0' }} />

      {/* Volume controls */}
      <div style={{ padding: '6px 12px 4px' }}>
        <div style={{
          fontSize: 9, fontFamily: 'var(--font-label)',
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: 'var(--color-ink-muted)', marginBottom: 6,
        }}>
          Volume
        </div>

        {/* Chord volume */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', marginBottom: 8,
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 10,
            color: 'var(--color-ink-secondary)', width: 40,
          }}>
            Chords
          </span>
          <VolumeSlider
            value={chordVolume}
            onChange={onChordVolume}
            title="Chord volume"
            color="var(--color-accent)"
          />
        </div>

        {/* Click volume — only shown when metronome is on */}
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          opacity: showClick ? 1 : 0.35,
          transition: 'opacity 150ms',
        }}>
          <span style={{
            fontFamily: 'var(--font-mono)', fontSize: 10,
            color: 'var(--color-ink-secondary)', width: 40,
          }}>
            Click
          </span>
          <VolumeSlider
            value={clickVolume}
            onChange={onClickVolume}
            title={showClick ? 'Click volume' : 'Turn on metronome to adjust click volume'}
            color="var(--color-accent)"
          />
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="playback-controls flex items-center gap-1">

      {/* Instrument / volume selector */}
      <button
        ref={buttonRef}
        onClick={handleToggle}
        title="Instrument & volume"
        className="flex items-center gap-1 h-7 px-2 rounded border border-rule text-[11px] font-mono text-ink-secondary hover:text-ink-primary hover:bg-hover transition-colors"
      >
        {synthMode === 'guitar' ? '🎸' : '🎹'}
        <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" className="opacity-50">
          <path d="M1 2l3 4 3-4" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {dropdown}

      {/* BPM input */}
      <div className="flex items-center gap-0.5" title="Tempo (BPM)">
        <span className="text-[9px] font-label tracking-widest uppercase text-ink-muted select-none">BPM</span>
        <input
          type="number"
          min={20}
          max={400}
          value={bpmInput}
          onChange={(e) => setBpmInput(e.target.value)}
          onBlur={commitBpm}
          onKeyDown={(e) => {
            if (e.key === 'Enter')  { commitBpm(); (e.target as HTMLInputElement).blur(); }
            if (e.key === 'Escape') { setBpmInput(String(parseBpmFromTempo(tempo))); (e.target as HTMLInputElement).blur(); }
            e.stopPropagation();
          }}
          className={[
            'bpm-input w-12 h-7 px-1 text-center text-[12px] font-mono',
            'bg-surface-2 border border-rule rounded',
            'text-ink-primary focus:outline-none focus:border-accent',
            'transition-colors [appearance:textfield]',
            '[&::-webkit-outer-spin-button]:appearance-none',
            '[&::-webkit-inner-spin-button]:appearance-none',
          ].join(' ')}
        />
      </div>

      {/* Metronome toggle */}
      <button
        onClick={onToggleClick}
        title={showClick ? 'Metronome on — click to turn off' : 'Metronome off — click to turn on'}
        className={[
          'flex items-center justify-center w-7 h-7 rounded transition-colors text-[13px]',
          showClick
            ? 'text-accent border border-accent bg-accent/10'
            : 'text-ink-muted border border-rule hover:text-ink-secondary hover:bg-hover',
        ].join(' ')}
      >
        ♩
      </button>

      {/* Play / Stop */}
      <button
        onClick={onToggle}
        disabled={isLoading}
        title={playing ? 'Stop (Space)' : 'Play (Space)'}
        className={[
          'flex items-center justify-center w-8 h-8 rounded transition-colors',
          playing
            ? 'bg-accent text-white hover:bg-accent/80'
            : 'bg-transparent border border-rule text-ink-secondary hover:text-ink-primary hover:bg-hover',
          isLoading ? 'opacity-50 cursor-wait' : '',
        ].filter(Boolean).join(' ')}
      >
        {isLoading ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="animate-spin">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"
              strokeDasharray="20" strokeDashoffset="10" strokeLinecap="round" />
          </svg>
        ) : playing ? (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <rect x="2" y="2" width="4" height="10" rx="1" />
            <rect x="8" y="2" width="4" height="10" rx="1" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor">
            <polygon points="3,1 13,7 3,13" />
          </svg>
        )}
      </button>

    </div>
  );
}